/**
 * Freezing **granted actions** — the action ids a combatant's systems/weapons grant, their RAW
 * usage caps (frequency grains and `Limited {N}`), and a frame's integrated weapon ids.
 */

import type { Compendium } from "@lancer-vtt/compendium";
import type { Affordance, AffordanceCap, AffordanceLimited, AffordanceSource, PilotData, WeaponRange } from "@lancer-vtt/domain";
import { affordanceKey } from "@lancer-vtt/domain";
import { gatherActions, type LoadedOverlay } from "../effects/loader";
import { parseRange, type RawWeapon } from "../weaponProfile";
import { isTechAttackAction } from "../techAttackAction";

/** The minimal shape the action freeze reads off a compendium system/weapon: its `actions[]` array, a system's `deployables[]` (the deploy-affordance source), and its own item-level `tags` (RAW's `tg_limited` lives on the SYSTEM, never on `actions[]` — {@link freezeActionCaps}'s item-level fallback reads this). */
export type RawActionBearer = { name?: unknown; actions?: unknown; deployables?: unknown; tags?: { id?: unknown; val?: unknown }[] };

/**
 * A compendium weapon as an action bearer: its own `actions[]`, then each firing profile's
 * `actions[]` in profile order. The Assault Cannon and the Leviathan carry their Spin Up and Cease
 * Spin-Up actions on their profiles, not the weapon, so without this they mint no row. `:N` indexes
 * this flattened list everywhere a weapon's granted id is minted or read ({@link freezeActionIds},
 * {@link freezeActionCaps}, `actionRegistry.ts`'s `lookupGranted`). A weapon with no profile actions
 * is returned unchanged.
 */
export function weaponActionBearer(item: RawActionBearer | undefined): RawActionBearer | undefined {
  const profiles = (item as { profiles?: unknown } | undefined)?.profiles;
  if (!item || !Array.isArray(profiles)) return item;
  const profileActions = profiles.flatMap((p) => (Array.isArray((p as { actions?: unknown })?.actions) ? ((p as { actions: unknown[] }).actions) : []));
  if (profileActions.length === 0) return item;
  return { ...item, actions: [...(Array.isArray(item.actions) ? item.actions : []), ...profileActions] };
}

/** A raw compendium item's display name, when it carries a non-empty one. */
export function rawItemName(item: RawActionBearer | undefined): string | undefined {
  return typeof item?.name === "string" && item.name !== "" ? item.name : undefined;
}

/** Optional inputs to {@link freezeActionIds}. */
export type FreezeActionIdsOpts = {
  /** The frame's integrated weapon ids, walked first. */
  integratedWeaponIds?: string[];
  /** The mech's frozen Sensors — the reach of a rangeless drone deploy row. */
  sensors?: number;
};

/**
 * Freeze the mech's **loadout-granted action ids** for combat — the id half of the granted
 * {@link "@lancer-vtt/domain".Affordance} records {@link buildAffordances} assembles onto
 * `CombatantBase.affordances`. Walks
 * the active loadout's equipped **systems** and
 * **weapons** (plus the frame-integrated weapons), resolves each out of the compendium **once**, and —
 * for every item carrying an `actions[]` array — emits the composite overlay id `${itemId}:${index}`
 * (one per action). This mirrors {@link freezeWeapons} (it reads the compendium for each item, unlike
 * the pure-over-`PilotData` {@link freezePilotEffectIds}) — it needs each item's `actions[]`.
 *
 * **Ids-only, all of them**: every granted id freezes here whether or not an authored overlay body
 * exists — `resolvable` is *derived* downstream (`SEED_OVERLAY[id]` has an `action` effect), never
 * frozen. Display copy never enters combat state.
 *
 * **Honest degrade**: an item id that doesn't resolve in the spawn driver's
 * compendium (a pack it didn't load) is skipped — no id frozen — exactly like an unresolved weapon in
 * {@link freezeWeapons}. The enumerator/gather treat a missing id as a clean no-op, never a crash.
 *
 * Order: integrated weapons, then loadout-mounted weapons (mount/slot order), then equipped systems
 * (equip order) — the enumerator keys by id, so order is for determinism only. An empty result (`[]`)
 * ⇒ the mech carries no action-bearing gear; the caller omits the field rather than freezing `[]`.
 *
 * **`techAttackActions`** — a THIRD sparse map, frozen beside
 * `ranges`: for every minted id whose RAW `actions[]` entry declares `attack === "tech"` (a system
 * whose own RAW ability IS a tech attack — Markerlight, and the licensed family behind it), records
 * that entry's `activation` (`"Quick Tech"`/`"Full Tech"` in `lancer-data`'s prose casing, normalized
 * to `"quick_tech"`/`"full_tech"`). This is the ONE frozen fact `legalTechAttackWeaponIds` (the
 * legality wall), `actionRegistry` (the bar route), and `actionIdForAttackLike`/`intentCost` (the
 * economy) all read — no second derivation, no drift.
 * Sparse like `ranges`: an action with no `attack: "tech"` never lands a key; a caller with an empty
 * map omits the field entirely, mirroring `actionRanges`. Universal across the
 * walk (integrated weapons, loadout weapons, equipped systems) and NOT special-cased to index `:0` —
 * `ms_aggressive_system_sync`/`ms_law_of_blades` each carry two tech-attack actions (`:0` and `:1`)
 * on one item, so the mint must address both correctly the day they're pack-authored.
 */
export function freezeActionIds(
  mech: PilotData["mechs"][number],
  compendium: Compendium,
  opts: FreezeActionIdsOpts = {},
): {
  ids: string[];
  sources: AffordanceSource[];
  /** Each minted item's display name, keyed by item id (`Affordance.name`). */
  names: Record<string, string>;
  ranges: Record<string, WeaponRange[]>;
  techAttackActions: Record<string, { activation: "quick_tech" | "full_tech" }>;
} {
  const { integratedWeaponIds = [], sensors } = opts;
  const ids: string[] = [];
  // The STRUCTURED half of the same mint, positionally parallel to `ids` — `ids[n]` is always
  // `affordanceKey(sources[n])`. Minted here rather than recovered by parsing `ids` downstream:
  // the walk already holds the item id and the affordance-space index, and `decisions/0176`'s
  // whole point is that a reader should never have to re-derive the class from the string.
  const sources: AffordanceSource[] = [];
  const names: Record<string, string> = {};
  // The reach of each minted id whose RAW action declares one (`CombatantBase.actionRanges`) — sparse,
  // so an action with no `range` simply never lands a key (absent ⇒ ungated).
  const ranges: Record<string, WeaponRange[]> = {};
  // The frozen tech-attack activation of each minted id whose RAW action declares `attack: "tech"`
  // (`CombatantBase.techAttackActions`) — sparse, same discipline as `ranges` above.
  const techAttackActions: Record<string, { activation: "quick_tech" | "full_tech" }> = {};
  // Mint a composite `<itemId>:<index>` id per `actions[]` entry; returns whether the item
  // contributed any (so the system walk can decide whether a deploy affordance would double-mint).
  const collectActions = (item: RawActionBearer | undefined, itemId: string): boolean => {
    if (!item || !Array.isArray(item.actions)) return false; // unresolved / actionless item → honest no-op
    const name = rawItemName(item);
    if (name !== undefined && item.actions.length > 0) names[itemId] = name;
    for (let i = 0; i < item.actions.length; i++) {
      const source: AffordanceSource = { kind: "granted", itemId, index: i };
      const id = affordanceKey(source);
      ids.push(id);
      sources.push(source);
      const entry = item.actions[i] as { range?: unknown; attack?: unknown; activation?: unknown } | undefined;
      // Same parser the weapon freeze uses, so one vocabulary of reach across attacks and actions.
      const range = parseRange(entry?.range as RawWeapon["range"]);
      if (range.length > 0) ranges[id] = range;
      if (isTechAttackAction(entry)) {
        const activation = normalizeTechActivation(entry?.activation);
        if (activation) techAttackActions[id] = { activation };
      }
    }
    return item.actions.length > 0;
  };
  // Systems ALSO surface a **deploy affordance** per `deployables[]` entry — a deploy-only system
  // (Jericho, turret) carries `deployables[]` with zero `actions[]`, so nothing else would mint a
  // bar row for it. The composite id resolves to the authored overlay through the loader's
  // bare-id fallback, exactly like an `actions[]` id.
  //
  // **Offset, not dedupe:** the deploy mint OFFSETS past the item's `actions[]` ids,
  // `<itemId>:<actions.length + i>`, so `:N` addresses a position in the item's unified affordance
  // space (`actions[]` first, then `deployables[]`) rather than colliding with it. `gatherActions`
  // (`effects/loader.ts`) indexes by `:N` into the entry's `kind:"action"` clauses, so a
  // both-arrays system (smoke/hex charges) mints two DISTINCT ids that resolve to two DISTINCT
  // clauses — dedupe would instead have silently given two rows the same clause. Deploy-only
  // systems (Jericho, turret) have `actions.length === 0`, so their offset is 0. An unauthored
  // deployable clause honest-degrades to the advisory row downstream.
  const collectSystem = (item: RawActionBearer | undefined, itemId: string) => {
    collectActions(item, itemId);
    const actionCount = item && Array.isArray(item.actions) ? item.actions.length : 0;
    if (item && Array.isArray(item.deployables)) {
      for (let i = 0; i < item.deployables.length; i++) {
        const source: AffordanceSource = { kind: "granted", itemId, index: actionCount + i };
        const id = affordanceKey(source);
        ids.push(id);
        sources.push(source);
        // A drone with no stated range deploys "within Sensors" (`tg_drone`), so its deploy row's
        // reach is the deployer's Sensors: the hover wash and `checkActivateSiteRange` read it.
        const deployable = item.deployables[i] as { type?: unknown; range?: unknown } | undefined;
        const range = parseRange(deployable?.range as RawWeapon["range"]);
        if (range.length > 0) ranges[id] = range;
        else if (deployable?.type === "Drone" && sensors !== undefined && sensors > 0) ranges[id] = [{ kind: "range", val: sensors }];
      }
      const name = rawItemName(item);
      if (name !== undefined && item.deployables.length > 0) names[itemId] = name;
    }
  };
  for (const id of integratedWeaponIds) collectActions(weaponActionBearer(compendium.getById("weapon", id) as RawActionBearer | undefined), id);
  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  if (loadout) {
    for (const mount of loadout.mounts) {
      for (const slot of mount.slots) {
        if (slot.weaponId) collectActions(weaponActionBearer(compendium.getById("weapon", slot.weaponId) as RawActionBearer | undefined), slot.weaponId);
      }
    }
    for (const systemId of loadout.systems ?? []) {
      collectSystem(compendium.getById("system", systemId) as RawActionBearer | undefined, systemId);
    }
  }
  return { ids, sources, names, ranges, techAttackActions };
}

/**
 * Fill `ranges` from the overlay for each granted key the compendium gave no reach: an action clause's
 * authored `reach` (`ActionEffect.reach`), for RAW that states it only in prose (Stasis Generator's
 * "line of sight and range 5", Hunter Lock's "within Sensors"). `"sensors"` freezes `sensors`.
 * Returns a new map; a key with a compendium range keeps it.
 */
export function freezeAuthoredReach(args: {
  ownerId: string;
  keys: readonly string[];
  ranges: Record<string, WeaponRange[]>;
  overlay: LoadedOverlay;
  sensors: number | undefined;
}): Record<string, WeaponRange[]> {
  const { ownerId, keys, ranges, overlay, sensors } = args;
  const out = { ...ranges };
  for (const key of keys) {
    if (out[key] !== undefined) continue;
    const reach = gatherActions({ ownerId, entityId: key, overlay }).find((a) => a.effect.reach !== undefined)?.effect.reach;
    const val = reach === "sensors" ? sensors : reach;
    if (val !== undefined && val > 0) out[key] = [{ kind: "range", val }];
  }
  return out;
}

/**
 * Normalize a RAW `activation` string (`lancer-data`'s prose casing — `"Quick Tech"`/`"Full Tech"`)
 * into the `CombatantBase.techAttackActions` vocabulary. `null` for anything else (an action's
 * `attack === "tech"` with a missing/unrecognized `activation` honest-degrades to "not frozen"
 * rather than guessing — {@link freezeActionIds}'s caller only writes an entry when this returns
 * non-null). Verified against real data (`reference/lancer-data/lib/systems.json`,
 * `reference/lancer-data/lib/talents.json`): every shipped `attack: "tech"` entry across the 8
 * corpus carriers uses exactly one of these two strings.
 */
export function normalizeTechActivation(raw: unknown): "quick_tech" | "full_tech" | null {
  if (typeof raw !== "string") return null;
  const norm = raw.trim().toLowerCase().replace(/\s+/g, "_");
  return norm === "quick_tech" || norm === "full_tech" ? norm : null;
}

/** The subset of a compendium action entry {@link freezeActionCaps} reads: its RAW `frequency` string, and (defensively — no shipped content exercises this yet) its own `tags` (a granted action's own `Limited {N}`, distinct from a weapon's `WeaponProfile.limited`). */
type RawActionEntry = { frequency?: string; tags?: { id?: unknown; val?: unknown }[] };

/**
 * Parse a RAW frequency string (`"1/turn"`, `"2/round"`, `"1/scene"`) into the usage-cap
 * descriptor grain a frozen {@link "@lancer-vtt/domain".Affordance}'s `cap` carries. `null` for an
 * absent/unrecognized string (an ordinary unlimited granted action — the common case).
 */
export function parseActionFrequency(freq: string | undefined): { grain: "turn" | "round" | "scene"; max: number } | null {
  if (!freq) return null;
  const m = /^(\d+)\s*\/\s*(turn|round|scene)$/i.exec(freq.trim());
  if (!m) return null;
  const max = Number(m[1]);
  if (!Number.isFinite(max) || max <= 0) return null;
  return { grain: m[2]!.toLowerCase() as "turn" | "round" | "scene", max };
}

/**
 * Read a `Limited {N}` charge count off a `tags` array (`tg_limited`) — usable both for a
 * granted action's own `tags` (rare — no shipped content authors this at the action level) and
 * for the OWNING ITEM's `tags` ({@link freezeActionCaps}'s item-level arm below), the
 * shape RAW actually uses: `tg_limited` lives on the SYSTEM/WEAPON compendium entry, never on its
 * `actions[]` (verified against `@massif/lancer-data`: 27 core systems carry item-level
 * `tg_limited`). Distinct from a weapon's `WeaponProfile.limited`: weapon Limited rides the
 * weapon-fire store, keyed by `weaponInstanceKey`; a granted action's Limited has no weapon
 * instance to key off, so it rides the record's own `limited` pool (`decisions/0202`).
 *
 * The pool is a separate fact from a RAW `frequency` rate, and the two compose: an action can carry
 * both (AGNI-Class NHP is `tg_limited 1` with a `1/scene` action). The presence of `limited` is what
 * lets the commit extractor ({@link "@lancer-vtt/domain".limitedSystemUsedCounts}) persist a
 * Limited system's consumed charge across a commit/redeploy while leaving an ordinary "1/scene"
 * rate to reset for free every encounter, and what `spend_repairs track:"limited"` replenishes
 * (RAW: Limited refreshes on Full Repair, not per encounter).
 */
function actionLimited(tagged: { tags?: { id?: unknown; val?: unknown }[] }): AffordanceLimited | null {
  const tag = tagged.tags?.find((t) => t.id === "tg_limited");
  return tag && typeof tag.val === "number" && tag.val > 0 ? { max: tag.val } : null;
}

/** The two frozen economy facts a spawn freezer produces, keyed by affordance key: rates and Limited pools (`decisions/0202`). */
export type FrozenEconomyFacts = {
  caps: Record<string, AffordanceCap>;
  limited: Record<string, AffordanceLimited>;
};

/**
 * Freeze the mech's **granted-action rates and Limited pools** — mirrors {@link
 * freezeActionIds}'s walk (integrated weapons, loadout weapons, equipped systems),
 * **id-mint-for-id-mint including its `collectSystem` deploy OFFSET branch**, so every id
 * that walk mints for a system carrying `deployables[]` (Turret Drones, Aegis Shield
 * Generator, Smoke/HEX Charges' mine row) can also pick up a pool here — covering only the
 * `actions[]` path would silently leave those ids uncapped.
 *
 * For each granted composite id it freezes **both** facts independently (`decisions/0202`): the
 * action's RAW `frequency` as a `cap` rate, and a `Limited {N}` pool from the action's own `tags`
 * or, failing that, the OWNING ITEM's `tags`. An action with none contributes no entry
 * (unrestricted — the common case, e.g. every shipped Protocol action without a `frequency`). The
 * item-level arm is the one that matters in shipped content (see {@link actionLimited}) — RAW puts
 * `Limited {N}` on the system, not the action.
 *
 * **Charge pooling.** Offset addressing means one item can mint MORE THAN ONE composite id
 * (Smoke/HEX Charges: a thrown-grenade `actions[]` id AND a deployable-mine `deployables[]` id).
 * Both draw from the SAME RAW charge pool, so an item-level pool carries `pool: itemId` —
 * `spend_action_use`/`checkGrantedActionLegality` then key its consumed count on `pool ?? key`, so
 * every id sharing one item's pool drains one counter. That also keeps the pool off the scene-grain
 * rate's slot (`key`), so an AGNI-shaped `1/scene` plus `Limited 1` counts each fact once. **A rate
 * is never pooled** — it is per-action by RAW construction (five shipped systems carry actions with
 * differing frequencies). An action's own `tg_limited` pools under the action's key alone; no
 * shipped content authors one, let alone beside a scene-grain frequency on the same action.
 */
export function freezeActionCaps(
  mech: PilotData["mechs"][number],
  compendium: Compendium,
  integratedWeaponIds: string[] = [],
): FrozenEconomyFacts {
  const out: FrozenEconomyFacts = { caps: {}, limited: {} };
  // Mint each `actions[]` entry's rate and pool; returns the item's own `actions.length` (0 for an
  // unresolved/actionless item), which `collectSystem` below needs to OFFSET its deploy mint past.
  const collect = (item: RawActionBearer | undefined, itemId: string): number => {
    if (!item || !Array.isArray(item.actions)) return 0; // unresolved / actionless item → honest no-op
    const itemLimited = actionLimited(item); // item-level `tg_limited` — the RAW-real path
    (item.actions as RawActionEntry[]).forEach((a, i) => {
      const key = `${itemId}:${i}`;
      const rate = parseActionFrequency(a?.frequency);
      if (rate) out.caps[key] = rate;
      const limited = actionLimited(a ?? {}) ?? (itemLimited ? { ...itemLimited, pool: itemId } : null);
      if (limited) out.limited[key] = limited;
    });
    return item.actions.length;
  };
  // Systems ALSO mirror `freezeActionIds`'s deploy branch: a system with a `deployables[]`
  // mints one composite id per deployable there, OFFSET past `actions[]`, and if the item itself
  // is Limited, every one of those ids shares that same item-level pool with the `actions[]` ids
  // off the SAME item.
  const collectSystem = (item: RawActionBearer | undefined, itemId: string) => {
    const actionCount = collect(item, itemId);
    if (!item || !Array.isArray(item.deployables)) return;
    const itemLimited = actionLimited(item);
    if (!itemLimited) return;
    for (let i = 0; i < item.deployables.length; i++) out.limited[`${itemId}:${actionCount + i}`] = { ...itemLimited, pool: itemId };
  };
  for (const id of integratedWeaponIds) collect(weaponActionBearer(compendium.getById("weapon", id) as RawActionBearer | undefined), id);
  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  if (loadout) {
    for (const mount of loadout.mounts) {
      for (const slot of mount.slots) {
        if (slot.weaponId) collect(weaponActionBearer(compendium.getById("weapon", slot.weaponId) as RawActionBearer | undefined), slot.weaponId);
      }
    }
    for (const systemId of loadout.systems ?? []) {
      collectSystem(compendium.getById("system", systemId) as RawActionBearer | undefined, systemId);
    }
  }
  return out;
}

/**
 * The frame-integrated weapon ids a frame deploys automatically (the Barbarossa's
 * `core_system.integrated` Apocalypse Rail). Reads the frame's
 * `integrated` and `core_system.integrated` id lists (both observed in core data),
 * returning only the well-formed string ids; a frame with no integrated weapons → `[]`.
 */
export function frameIntegratedWeaponIds(frame: Record<string, unknown> | undefined): string[] {
  if (!frame) return [];
  const ids: string[] = [];
  const collect = (v: unknown) => {
    if (Array.isArray(v)) for (const x of v) if (typeof x === "string") ids.push(x);
  };
  collect(frame["integrated"]);
  const cs = frame["core_system"];
  if (cs && typeof cs === "object") collect((cs as Record<string, unknown>)["integrated"]);
  return ids;
}

/**
 * Freeze an **affordance record for each overlay-minted granted action** whose authored `action`
 * clause carries the {@link "../effects/atoms".LimitedUses} facet — the third arm of the
 * affordance freeze, beside {@link freezeActionCaps} (compendium `actions[]`/`deployables[]`) and
 * `freezeFrameTraitCaps` (frame traits).
 *
 * **The hole it closes.** {@link freezeActionCaps} walks the COMPENDIUM item's
 * `actions[]`/`deployables[]` arrays, so an id that exists only as `carriedGrantedActionIds`'
 * enumerate-time overlay mint can never pick one up. `ms_custom_paint_job` is the worked case: its
 * `lancer-data` record carries no `actions[]`, no `deployables[]` and no `tg_limited`, so
 * `ms_custom_paint_job:0` is a pure overlay mint and this facet is the ONLY possible source for
 * its RAW "once before each Full Repair" charge (`projects/track-damage-event-family.md` D2/D3).
 *
 * **It is the same walk `carriedGrantedActionIds` does** (`economy/carriedActions.ts`), reusing
 * {@link gatherActions} over each equipped system's BARE id and indexing its `kind:"action"`
 * clauses by position — not a second derivation that could drift from the id set the bar and the
 * activate wall agree on. Two properties are inherited from that walk deliberately:
 *   - **`frozenKeys` dedupe** — a `:N` position already minted by {@link freezeActionIds}
 *     (from the compendium arrays) is skipped here, exactly as `carriedGrantedActionIds` skips it,
 *     so the compendium-sourced cap always wins and one id never gets two competing caps.
 *   - **scoped to `equippedSystems`** — the frozen list, so this can only ever produce a cap for a
 *     system the combatant genuinely equips (including a talent-integrated one, which
 *     `loadout.systems` alone would miss).
 *
 * **The fact is fixed, not authored**: a `limited: {max}` pool and no rate. The pool is the whole
 * point — it is what `limitedSystemUsedCounts` reads off the FROZEN record to persist the consumed
 * charge across a commit/redeploy, so it clears on **Full Repair** rather than free every encounter. A clause with no `limitedUses` contributes nothing (uncapped — the common case), and
 * an unauthored/absent overlay entry honest-degrades to no clauses at all.
 *
 * **This arm freezes RECORDS, not just caps** (`affordance-table` Phase 3.1). It used to freeze
 * only a cap, into a map keyed by an id `freezeActionIds` never minted — which was fine while
 * `base.actionUsageCaps` was a flat string-keyed map that nothing had to be able to enumerate.
 * With one record per affordance, a frozen cap whose record is missing is unreadable: the
 * structure-loss interception (`structureIntercept.ts`) and the System-Trauma destroyability read
 * (`tables.ts`) both find `ms_custom_paint_job`'s charge by walking `base.affordances` for
 * `source.itemId`, and neither has an overlay in scope to re-mint from. So the record is frozen
 * here beside its cap. `carriedAffordances`' call-time mint dedupes against the frozen keys, so
 * the row it produces for the bar is unchanged — it now comes from the frozen half instead.
 */
export function freezeOverlayAffordances(args: {
  ownerId: string;
  equippedSystems: readonly { id: string }[];
  frozenKeys: readonly string[];
  overlay: LoadedOverlay;
}): Affordance[] {
  const { ownerId, equippedSystems, frozenKeys, overlay } = args;
  const frozen = new Set(frozenKeys);
  const out: Affordance[] = [];
  for (const sys of equippedSystems) {
    // A bare-id gather returns EVERY `kind:"action"` clause the overlay authors for this entry, in
    // authored order — its length IS the system's authored affordance count, and position `i` is
    // the `:N` the mint addresses (mirrors `carriedAffordances`).
    const clauses = gatherActions({ ownerId, entityId: sys.id, overlay });
    clauses.forEach((clause, i) => {
      const source = { kind: "granted", itemId: sys.id, index: i } as const;
      const key = affordanceKey(source);
      if (frozen.has(key) || out.some((a) => a.key === key)) return; // compendium-frozen id, or a duplicate equip of the same system
      const max = clause.effect.limitedUses?.max;
      if (max === undefined) return; // no authored cap — uncapped, the common case
      out.push({ key, source, limited: { max } });
    });
  }
  return out;
}

/**
 * The **effect-class records a talent's authored `limitedUses` freezes** — the talent-rank sibling
 * of {@link freezeOverlayAffordances}' granted arm. Each pilot effect id (a talent rank, `t_leader:1`)
 * whose overlay entry authors an `action` clause with `limitedUses` mints one record keyed by the
 * bare id, the address `resolveAffordance`'s exact arm finds for an effect-class activation, so the
 * pool rides the same `checkGrantedActionLegality` gate and `spend_action_use` fold as any Limited
 * system. The pool persists across a commit through `limitedSystemUsedCounts` and clears on Full
 * Repair.
 *
 * Records sharing a `pool` all freeze the largest `max` authored for it: Leader rank 2's "Gain 5
 * Leadership Dice instead of 3" resizes the one pool both ranks spend from.
 */
export function freezeTalentAffordances(args: { ownerId: string; effectIds: readonly string[]; overlay: LoadedOverlay }): Affordance[] {
  const { ownerId, effectIds, overlay } = args;
  const out: Affordance[] = [];
  for (const id of new Set(effectIds)) {
    const authored = gatherActions({ ownerId, entityId: id, overlay }).find((c) => c.effect.limitedUses !== undefined)?.effect.limitedUses;
    if (authored === undefined) continue;
    out.push({ key: id, source: { kind: "effect", entityId: id }, limited: { max: authored.max, ...(authored.pool !== undefined ? { pool: authored.pool } : {}) } });
  }
  const poolMax = new Map<string, number>();
  for (const a of out) if (a.limited?.pool !== undefined) poolMax.set(a.limited.pool, Math.max(poolMax.get(a.limited.pool) ?? 0, a.limited.max));
  return out.map((a) => (a.limited?.pool !== undefined ? { ...a, limited: { ...a.limited, max: poolMax.get(a.limited.pool)! } } : a));
}

/**
 * The **Limited pool of a system with no affordance**: an equipped system tagged `tg_limited {N}`
 * whose charges no granted record already pools (no `actions[]`, no `deployables[]`, no authored
 * `limitedUses`). Walking Armory's Ammo Case is the case: its charges are spent from an attack
 * trigger (`expend_charges`), not by activating it. One effect-class record per system,
 * keyed and pooled on the system id, so it never becomes a bar row (`carriedAffordances` lists
 * granted records only) and the commit extractor persists it like any Limited pool.
 */
export function freezeLimitedSystemPools(args: { equippedSystems: readonly { id: string; tags?: readonly { id: string; val?: number }[] }[]; frozen: readonly Affordance[] }): Affordance[] {
  const { equippedSystems, frozen } = args;
  const out: Affordance[] = [];
  for (const sys of equippedSystems) {
    const max = sys.tags?.find((t) => t.id === "tg_limited")?.val;
    if (max === undefined || max < 1) continue;
    const pooled = [...frozen, ...out].some((a) => (a.source.kind === "granted" && a.source.itemId === sys.id) || a.key === sys.id);
    if (pooled) continue;
    out.push({ key: sys.id, source: { kind: "effect", entityId: sys.id }, limited: { max, pool: sys.id } });
  }
  return out;
}

/**
 * Assemble the **frozen affordance table** ({@link "@lancer-vtt/domain".CombatantBase.affordances})
 * from a spawn walk's structured {@link AffordanceSource}s plus the cap/limited/recharge maps that walk's
 * siblings froze — the one place every spawn adapter (mech, pilot, NPC, and the on-foot pilot
 * block) turns "which affordances exist" + "which of them carry an economy fact" into records.
 *
 * **It is a JOIN, not a fourth derivation.** The sources come from the id mint itself; the rates,
 * Limited pools and recharge targets are per-adapter intermediates keyed by
 * {@link "@lancer-vtt/domain".affordanceKey}, joined here and never persisted as maps of their own
 * — `affordance-table` Phase 3.1 deleted `base.actionIds`/`actionUsageCaps`/`actionRecharge`, so
 * this join's output is the only frozen description of the economy. `affordanceTable.pin.test.ts`
 * pins the result positionally.
 *
 * **Order is the caller's order**, preserved verbatim — for the granted class that is
 * `freezeActionIds`' walk order (integrated weapons, loadout weapons, equipped systems), which is
 * what makes the pin a positional comparison rather than a set one.
 *
 * **Two facts, two maps** (`decisions/0202`): `caps` holds each affordance's RAW rate and `limited`
 * its Limited charge pool. A key may appear in both (Drop Bomb's `1/round` and `Limited 3`), and the
 * record carries both.
 *
 * A source with no cap, no pool and no recharge target yields a bare `{key, source}` record — unrestricted,
 * the common case — never a record carrying `undefined` fields.
 */
export function buildAffordances(args: {
  sources: readonly AffordanceSource[];
  caps?: Record<string, AffordanceCap>;
  limited?: Record<string, AffordanceLimited>;
  recharge?: Record<string, number>;
  /** Display names keyed by granted item id (`Affordance.name`). */
  names?: Record<string, string>;
}): Affordance[] {
  const { sources, caps, limited, recharge, names } = args;
  return sources.map((source) => {
    const key = affordanceKey(source);
    const cap = caps?.[key];
    const pool = limited?.[key];
    const rechargeTarget = recharge?.[key];
    const name = source.kind === "granted" ? names?.[source.itemId] : undefined;
    return {
      key,
      source,
      ...(cap ? { cap } : {}),
      ...(pool ? { limited: pool } : {}),
      ...(rechargeTarget !== undefined ? { recharge: rechargeTarget } : {}),
      ...(name !== undefined ? { name } : {}),
    };
  });
}
